import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { getListings, isHoodieApiConfigured } from "../../api/services/hoodieApi";
import {
  formatApiError,
  normalizeListingsResponse,
} from "./listingsWranglerUtils";
import AdminToolsMlistAuthStatus from "./AdminToolsMlistAuthStatus";

const Wrap = styled.div`
  width: 100%;
  padding: 24px 16px;
`;

const TopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: end;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const Title = styled.h1`
  font-size: 22px;
  margin: 0;
`;

const Sub = styled.div`
  color: #666;
  margin-top: 4px;
  font-size: 13px;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: end;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #444;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  min-width: 260px;
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
`;

const Button = styled.button`
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #d0d0d0;
  background: ${(p) => (p.$primary ? "#111" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#111")};
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? 0.55 : 1)};
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-size: 12px;
  color: #666;
  font-weight: 700;
  border-bottom: 1px solid #eee;
  padding: 10px;
`;

const Td = styled.td`
  border-bottom: 1px solid #f0f0f0;
  padding: 10px;
  vertical-align: top;
  font-size: 14px;
`;

const Empty = styled.div`
  padding: 14px;
  color: #666;
`;

const Notice = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #856404;
`;

function safeStr(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function tagSummary(tags) {
  if (!Array.isArray(tags)) return safeStr(tags) || "—";
  if (tags.length === 0) return "0 tags";
  const first = tags[0];
  if (typeof first === "object" && first?.name) {
    return `${tags.length} tag(s)`;
  }
  return `${tags.length} tag id(s)`;
}

export default function ListingsWranglerListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const qParam = searchParams.get("q") || "";
  const limitParam = Number(searchParams.get("limit") || "25");
  const offsetParam = Number(searchParams.get("offset") || "0");

  const [q, setQ] = useState(qParam);
  const [limit, setLimit] = useState(
    Number.isFinite(limitParam) && limitParam > 0 ? limitParam : 25
  );

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [rawData, setRawData] = useState(null);

  const offset =
    Number.isFinite(offsetParam) && offsetParam >= 0 ? offsetParam : 0;

  const normalized = useMemo(
    () => normalizeListingsResponse(rawData),
    [rawData]
  );

  const { results, count, isPaginated } = normalized;

  async function load(params) {
    setLoading(true);
    setErr(null);
    try {
      const res = await getListings(params);
      setRawData(res);
    } catch (e) {
      setErr(e);
      setRawData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isHoodieApiConfigured()) return;
    load({
      search: qParam || undefined,
      limit,
      offset,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qParam, limit, offset]);

  const canPrev = offset > 0;
  const canNext = isPaginated
    ? typeof count === "number"
      ? offset + results.length < count
      : Boolean(normalized.next)
    : results.length >= limit && results.length > 0;

  const pageStart = results.length ? offset + 1 : 0;
  const pageEnd = offset + results.length;

  function applyParams(next) {
    const sp = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") sp.delete(k);
      else sp.set(k, String(v));
    });
    setSearchParams(sp);
  }

  function onSubmit(e) {
    e.preventDefault();
    applyParams({ q, offset: 0, limit });
  }

  if (!isHoodieApiConfigured()) {
    return (
      <Wrap>
        <Title>Listings Data Wrangler</Title>
        <Empty>
          Hoodie API is not configured. Set{" "}
          <code>REACT_APP_HOODIE_API_BASE_URL</code> to enable{" "}
          <code>/api/hoodie/</code> calls.
        </Empty>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <TopRow>
        <div>
          <Title>Listings Data Wrangler</Title>
          <Sub>
            Legacy API: <code>/api/hoodie/listings/</code>
            {typeof count === "number" ? (
              <>
                {" "}
                • {count} total • showing {pageStart}-{pageEnd}
              </>
            ) : results.length ? (
              <> • {results.length} loaded (unpaginated response)</>
            ) : null}
            {!isPaginated && results.length > 0 ? (
              <Notice>
                API returned a bare array (not DRF pagination). Prev/Next use
                client offset only.
              </Notice>
            ) : null}
          </Sub>
        </div>

        <Controls as="form" onSubmit={onSubmit}>
          <Label>
            Search
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="business name, tag name, contact…"
            />
          </Label>
          <Label>
            Page size
            <Select
              value={limit}
              onChange={(e) => {
                const v = Number(e.target.value);
                setLimit(v);
                applyParams({ limit: v, offset: 0 });
              }}
            >
              {[10, 20, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </Label>
          <Button $primary type="submit" disabled={loading}>
            Search
          </Button>
          <Button
            type="button"
            disabled={loading}
            onClick={() => {
              setQ("");
              applyParams({ q: "", offset: 0 });
            }}
          >
            Clear
          </Button>
        </Controls>
      </TopRow>

      <div style={{ marginBottom: 10 }}>
        <Link to="/admin-tools">← Admin Tools</Link>
      </div>

      <AdminToolsMlistAuthStatus />

      <Card>
        {loading ? (
          <Empty>
            <em>Loading listings…</em>
          </Empty>
        ) : err ? (
          <Empty>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Error</div>
            <div style={{ marginBottom: 6 }}>{formatApiError(err)}</div>
            {err.status ? <div>Status: {err.status}</div> : null}
          </Empty>
        ) : results.length === 0 ? (
          <Empty>No results.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th style={{ width: 80 }}>ID</Th>
                <Th>Business</Th>
                <Th>Contact</Th>
                <Th>Email</Th>
                <Th>Tags</Th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.id}>
                  <Td>{r.id ?? "—"}</Td>
                  <Td>
                    <div style={{ fontWeight: 700 }}>
                      {r.id != null ? (
                        <Link to={`/admin-tools/listings/${r.id}`}>
                          {safeStr(r.business_name) || "(no name)"}
                        </Link>
                      ) : (
                        safeStr(r.business_name) || "(no name)"
                      )}
                    </div>
                    <div style={{ color: "#666", fontSize: 12 }}>
                      slug: {safeStr(r.slug) || "—"}
                    </div>
                  </Td>
                  <Td>
                    {safeStr(r.contact_name) || "—"}
                    {r.contact_title ? (
                      <div style={{ color: "#666", fontSize: 12 }}>
                        {safeStr(r.contact_title)}
                      </div>
                    ) : null}
                  </Td>
                  <Td>{safeStr(r.email) || "—"}</Td>
                  <Td>{tagSummary(r.tags)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        <Button
          disabled={loading || !canPrev}
          onClick={() => applyParams({ offset: Math.max(0, offset - limit) })}
        >
          Prev
        </Button>
        <Button
          disabled={loading || !canNext}
          onClick={() => applyParams({ offset: offset + limit })}
        >
          Next
        </Button>
      </div>
    </Wrap>
  );
}
